import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Supplier/SUPHeader';
import SidebarRight from '../components/Supplier/SUPSidebarRight';
import AddSupplierForm from '../components/Supplier/SUPAddSupplierForm';
import HomeBaseForm from '../components/Supplier/SUPHomeBaseForm';
import SupplierOnboard from '../components/Supplier/SUPSupplierOnboard';
import AdminDashboard from '../components/Supplier/SUPAdminDashboard';
import Cashier from '../components/Supplier/Cashier';
import api from '../api';

function SupplierSystem({
    user,
    token,
    handleLogout
}) {

    const [plants, setPlants] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [currentSupplier, setCurrentSupplier] = useState(null);
    const [currentTab, setCurrentTab] = useState(null);
    const [selectedVariety, setSelectedVariety] = useState(null);
    const [selectedCrop, setSelectedCrop] = useState(null);

    const prevSupplierId = useRef(null);

    const [currentDate, setCurrentDate] = useState(() => {
        const now = new Date();
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = monthNames[now.getMonth()];
        const day = String(now.getDate()).padStart(2, '0');
        const year = now.getFullYear();
        return `${month}-${day}-${year}`;
    });

    const initialSupplierData = {
        name: '',
        email: '',
        phone: '',
        logo: '',
        addressID: '',
        isNewSupplier: '',
        addressLine1: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
        plantID: '',
        plantType: '',
        supplierid: '',
        userid: '',
        role: '',
        userName: ''
    };

    const fetchSupplierPlants = async (supplierID) => {
        try {
            //xref for plantIDs - gardenerPlants
            const response = await api.get(`/supplierPlants/${supplierID}`);
            setPlants(response.data);
        } catch (err) {
            console.error('Error fetching plants:', err);
        }
    };

    const fetchCurrentSupplier = async () => {
        try {
            const response = await api.get('/supplier/current');

            const data = response.data;
            setCurrentSupplier(data);
            fetchSupplierPlants(data.id);
            return data;
        } catch (err) {
            throw err;
        }
    };

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
        });
    }, []);

    useEffect(() => {
        if (!currentSupplier) return;
        if (currentSupplier.id !== prevSupplierId.current) {
            setSelectedCrop(null);
            setSelectedVariety(null);
            prevSupplierId.current = currentSupplier.id;
        }
    }, [currentSupplier]);

    useEffect(() => {

        const fetchCurrentSupplierOnMount = async () => {
            if (!user?.isSupplier) return;
            if (!token) return;

           // fetch current supplier and wait for the result
           const supplier = await fetchCurrentSupplier(false);

           // set initial tab based on whether supplier is new
           if (supplier?.isNewSupplier) {
                            setCurrentTab('Supplier'); // onboarding
                            return;
           }

           if (supplier.role === 'CASHIER') {
                            setCurrentTab('Cashier');
           } else {
                            setCurrentTab('Home'); // normal home
           }
        };

        fetchCurrentSupplierOnMount();
    }, [token, user]);

    useEffect(() => {
        const interceptor = api.interceptors.response.use(
            res => res,
            err => {
                if (err.response?.status === 401) {
                    handleLogout();
                }
                return Promise.reject(err);
            }
        );

        return () => {
            api.interceptors.response.eject(interceptor);
        };
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentTab]);


    const handleTabChange = (tab, triggeredBySystem = false) => {
        if (currentSupplier?.isNewSupplier && !triggeredBySystem) {
            alert('Please finish onboarding before accessing other features.');
            return;
        }
        setCurrentTab(tab);
    };

    const handleSelectCrop = (crop) => {
        setSelectedCrop(crop);
        setCurrentTab('Home');
    };


    const handleUpdateSupplier = async (updatedSupplier) => {
        const requestBody = {
            name: updatedSupplier.name,
            email: updatedSupplier.email,
            phone: updatedSupplier.phone,
            logoURL: updatedSupplier.logoURL,
            website: updatedSupplier.website,
            addressID: updatedSupplier.addressID,
            streetAddress: updatedSupplier.streetAddress,
            city: updatedSupplier.city,
            province: updatedSupplier.province,
            postalCode: updatedSupplier.postalCode,
            country: updatedSupplier.country,
            isNewSupplier: updatedSupplier.isNewSupplier
        };


        try {
            await api.put(`/supplier/${currentSupplier.id}`, requestBody);
            fetchCurrentSupplier();
        } catch (err) {
            console.error('Failed to update supplier:', err);
        }
    };

    const handleAddPlant = async (plantData) => {
        const { plantID, plantType } = plantData;

        if (!plantID) {
            alert('Please select a plant to add.');
            return;
        }

        const numericPlantID = parseInt(plantID, 10);
        const plantInList = plants.some(listplant => listplant.id === numericPlantID);

        if (plantInList) {
            alert('Plant already exists in your list.');
            return;
        }

        try {
            await api.post('/supplierPlants/add', {
                supplierID: currentSupplier.id,
                plantID,
                plantType
            });
            fetchCurrentSupplier();
        } catch (err) {
            console.error('Failed to add plant:', err);
        }
    };

    const handleRemovePlant = async (plantID) => {

        if (!plantID) {
            alert('Please select a plant to remove.');
            return;
        }

        try {
            await api.delete('/supplierPlants/remove', {
                data: {
                    supplierID: currentSupplier.id,
                    plantID: plantID
                }
            });
            fetchCurrentSupplier();
        } catch (err) {
            console.error('Failed to remove plant:', err);

            if (err.response?.status === 400) {
                alert(err.response.data.error);
                return;
            }

            alert("Failed to remove plant.");
        }
    };

    // Still loading supplier therefore render nothing (or a spinner)
    if (!currentSupplier) {
        return null;
    }

    // New supplier therefore ONLY render onboarding, nothing else
    if (currentSupplier.isNewSupplier) {
        return (
            <div className="App">
                <Header
                    currentTab="Supplier"
                    handleTabChange={handleTabChange}
                    handleLogout={handleLogout}
                    currentSupplier={currentSupplier}
                    setCurrentSupplier={setCurrentSupplier}
                    isNewSupplierMode={true}
                />
                <div className="container">
                    <div className="main-content">
                        <div className="tab-container">
                            <SupplierOnboard
                                currentSupplier={currentSupplier}
                                handleTabChange={handleTabChange}
                                handleUpdateSupplier={handleUpdateSupplier}
                                handleAddPlant={handleAddPlant}
                                currentDate={currentDate}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    let renderedTabContent;

    if (currentTab === 'Home') {
        renderedTabContent = (
            <HomeBaseForm
                currentSupplier={currentSupplier}
                onSelectCrop={handleSelectCrop}
                selectedCrop={selectedCrop}
                plants={plants}
               handleTabChange={handleTabChange}
                fetchCurrentSupplier={fetchCurrentSupplier}
                currentDate={currentDate}
            />
        );
    } else if (currentTab === 'Supplier') {
        renderedTabContent = currentSupplier?.isNewSupplier ? (
            <SupplierOnboard
                currentSupplier={currentSupplier}
               handleTabChange={handleTabChange}
                handleUpdateSupplier={handleUpdateSupplier}
                handleAddPlant={handleAddPlant}
                currentDate={currentDate}
            />
        ) : (
            <AddSupplierForm
                currentSupplier={currentSupplier}
                fetchCurrentSupplier={fetchCurrentSupplier}
                handleUpdateSupplier={handleUpdateSupplier}
            />
        );
    
    } else if (currentTab === 'Admin') {
        renderedTabContent = (
            <AdminDashboard
                currentSupplier={currentSupplier}
                token={token}
                handleTabChange={handleTabChange}
            />
        );

    } else if (currentTab === 'Cashier') {
        renderedTabContent = (
            <Cashier
                currentSupplier={currentSupplier}
                token={token}
            />
        );
    }


    return (
        <div className="App">

                <Header
                    currentTab={currentTab}
                    handleTabChange={handleTabChange}
                    handleLogout={handleLogout}
                    currentSupplier={currentSupplier}
                    setCurrentSupplier={setCurrentSupplier}
                    isNewSupplierMode={currentSupplier?.isNewSupplier === true}
                />

            <div className={`container`}>
                <div className="main-content">
                    <div className={`tab-container`}>
                        {renderedTabContent}

                        {currentTab !== 'Cashier' && (

                            <SidebarRight
                                currentSupplier={currentSupplier}
                                plants={plants}
                                isLoggedIn={true}
                                onSelectCrop={handleSelectCrop}
                                selectedCrop={selectedCrop}
                                handleAddPlant={handleAddPlant}
                                handleRemovePlant={handleRemovePlant}
                                handleTabChange={handleTabChange}
                            />
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
}

export default SupplierSystem;
